import { Component, OnInit, AfterViewInit, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ICellRendererAngularComp } from "ag-grid-angular";
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { TimelineDatePipe } from "@app/pipes/timeline-date.pipe";
import { DatePipe } from "@app/pipes/date.pipe";
import { localDateFromPhpDateField, TimePipe } from "@app/pipes/time.pipe";

@Component({
  selector: 'app-cs-ag-grid-custom-cell-renderer',
  templateUrl: './cs-ag-grid-custom-cell-renderer.component.html',
  styleUrls: ['./cs-ag-grid-custom-cell-renderer.component.scss'],
})
export class CsAgGridCustomCellRendererComponent implements OnInit, AfterViewInit, OnDestroy {

    public params: any;
    public cellRenderData: any = [];
    public showFullText: boolean = false;

    @ViewChildren(TooltipDirective) tooltips: QueryList<TooltipDirective>;

    private scrollListener: (() => void) | null = null;

    constructor(
        private timelineDateTime: TimelineDatePipe,
        private shortDate: DatePipe,
        private timePipe: TimePipe,
        private sanitizer: DomSanitizer
    ) {
    }
    ngOnInit() {
        if (!this.params) {
            return;
        }
        if (this.params.data) {
            this.cellRenderData = this.params.data;
        } else if (this.params.node && this.params.node.group && this.params.value != null) {
            // Group row: no row data — build minimal object so keyMapping text fields can display
            const keyMapping =
                (this.params.cellRendererParams && this.params.cellRendererParams.keyMapping) ||
                this.params.keyMapping;
            if (keyMapping && keyMapping.length) {
                const first = keyMapping[0];
                if (first && first.text) {
                    this.cellRenderData = { [first.text]: this.params.value };
                }
            }
        }
    }

    ngAfterViewInit(): void {
        const api = this.params && this.params.api;
        if (api && typeof api.addEventListener === 'function') {
            this.scrollListener = () => this.hideTooltips();
            api.addEventListener('bodyScroll', this.scrollListener);
        }
    }

    ngOnDestroy(): void {
        if (this.scrollListener && this.params && this.params.api && typeof this.params.api.removeEventListener === 'function') {
            this.params.api.removeEventListener('bodyScroll', this.scrollListener);
            this.scrollListener = null;
        }
    }

    private hideTooltips(): void {
        if (this.tooltips) {
            this.tooltips.forEach(t => t.hide());
        }
    }

    hasContent(key: any, cellRenderData: any): boolean {
        return !!(
            key.profileImage ||
            (key.type === 'custom_icon' && key.iconsList[cellRenderData[key.iconKey]]) ||
            (key.type === 'static_icon' && key.iconName) ||
            (key.text && this.getTextValue(key, cellRenderData)) ||
            (key.customText && key.customTextList[cellRenderData[key.customTextKey]])
        );
    }

    /** Value for key.text; fallback to 'name' when key.text is 'File name' (files grid). Returns raw value (HTML or plain text). */
    getTextValue(key: any, cellRenderData: any): string | null {
        if (!key || !key.text || !cellRenderData) return null;
        const val = cellRenderData[key.text];
        const toTitleCase = (str: string): string => {
            return str
                .toLowerCase()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        };
        if (key.text === 'status') {
            const statusValue = typeof val === 'string' ? toTitleCase(val) : toTitleCase(String(val));
            return statusValue.replace(/_/g, ' ');

        }
        if (val != null && val !== '') return typeof val === 'string' ? val : String(val);
        if (key.text === 'File name' && cellRenderData['name'] != null && cellRenderData['name'] !== '') {
            const name = cellRenderData['name'];
            return typeof name === 'string' ? name : String(name);
        }
        return null;
    }

    /** Sanitize HTML for key.text when key.renderTextAsHtml is true. */
    getSanitizedTextValue(key: any, cellRenderData: any): SafeHtml | null {
        const val = this.getTextValue(key, cellRenderData);
        if (val == null || val === '' || !key || !key.renderTextAsHtml) return null;
        return this.sanitizer.bypassSecurityTrustHtml(val);
    }

    /** Plain text for tooltip when value may contain HTML (strip tags). */
    getTextValuePlain(key: any, cellRenderData: any): string | null {
        const val = this.getTextValue(key, cellRenderData);
        if (val == null || val === '') return null;
        if (key && key.renderTextAsHtml && /<[^>]+>/.test(val)) {
            return val.replace(/<[^>]*>/g, '').trim() || val;
        }
        return val;
    }

    /** Sanitize HTML for additionalText when key.renderAdditionalTextAsHtml is true. */
    getSanitizedAdditionalText(value: string, key: any): SafeHtml | null {
        if (value == null || value === '' || !key || !key.renderAdditionalTextAsHtml) return null;
        const str = typeof value === 'string' ? value : String(value);
        return this.sanitizer.bypassSecurityTrustHtml(str);
    }

    /** Whether Angular tooltip is enabled for this cell (cellRendererParams.tooltip !== false). */
    isTooltipEnabled(): boolean {
        const p = this.params && this.params.cellRendererParams;
        return p ? p.tooltip !== false : true;
    }

    refresh(params: any): boolean {
        throw new Error("Method not implemented.");
    }

    isDateObject(value: any): value is { date: string } {
        return value && typeof value === 'object' && 'date' in value;
    }

    getUserDate(val: string | { date: string }): string {
        let forPipes: Date | string;
        if (val && typeof val === 'object' && 'date' in val) {
            const d = localDateFromPhpDateField(val.date);
            if (!d) {
                return '';
            }
            forPipes = d;
        } else {
            forPipes = val as string;
        }
        const shortdate = this.shortDate.transform(forPipes);
        const usertime = this.timePipe.transform(forPipes);
        return shortdate + ' (' + usertime + ')';
    }

    getIconClass(iconValue: string | number): string {
        if (typeof iconValue === 'number' || !isNaN(Number(iconValue))) {
            const id = typeof iconValue === 'number' ? iconValue : parseInt(iconValue, 10);
            if(id==4 || id==5){
                return 'ios.svg';
            }
            return this.getIconById(id);
        }else{ // return as-is if it's already a string (class name)
            if(iconValue=='fa fa-apple'){
                return 'ios.svg';
            }else{
                return iconValue;
            }
        }

    }

    private getIconById(id: number): string {
        const createdByTypes = this.getCreatedbyType();
        if (isNaN(id)) {
            return '';
        }
        const foundType = createdByTypes.find(item => item.id === id.toString());
        return foundType ? foundType.icon : 'fa-regular fa-cloud';
    }

    private getCreatedbyType(): { id: string; text: string; icon: string }[] {
        return [
            {id: '1', text: 'System', icon: 'fa-sharp fa-regular fa-globe'},
            {id: '2', text: 'Online', icon: 'fa-sharp fa-regular fa-globe'},
            {id: '3', text: 'Webservice', icon: 'fa-sharp fa-regular fa-globe'},
            {id: '4', text: 'iPhone', icon: 'fa fa-apple'},
            {id: '5', text: 'iPad', icon: 'fa fa-apple'},
            {id: '6', text: 'Android tablet', icon: 'fa fa-brands fa-android'},
            {id: '8', text: 'Android phone', icon: 'fa fa-brands fa-android'},
        ];
    }

    getCustomIconColor(key: any, cellRenderData: any): string {
        if (!key || key.type !== 'custom_icon' || !key.iconsList || !cellRenderData) {
            return '';
        }

        const iconKey = cellRenderData[key.iconKey];
        const iconData = key.iconsList[iconKey];

        return iconData && iconData.color ? iconData.color : '';
    }

}
